import React, { useState, useEffect } from 'react';
import { analytics } from '@/lib/analytics';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEO } from "@/components/SEO";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { FloatingPaths } from '@/components/ui/floating-paths';
import { auth } from '@/lib/firebase';

import {
  ChevronLeftIcon,
  Mail,
  Lock,
  User,
  Building2,
  CheckCircle2,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';


const AuthSeparator = () => {
  return (
    <div className="flex w-full items-center justify-center my-8">
      <div className="bg-slate-200 h-px w-full" />
      <span className="text-slate-400 px-4 text-[10px] font-bold uppercase tracking-widest">OR</span>
      <div className="bg-slate-200 h-px w-full" />
    </div>
  );
};

const GENERIC_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com',
  'aol.com', 'icloud.com', 'mail.com', 'protonmail.com',
  'zoho.com', 'yandex.com', 'live.com', 'msn.com',
  'rediffmail.com', 'tutanota.com',
];
const EMPLOYER_EMAIL_EXCEPTIONS = (import.meta.env.VITE_SUPER_ADMIN_EMAIL ? [import.meta.env.VITE_SUPER_ADMIN_EMAIL as string] : []);
const PENDING_EMAIL_VERIFICATION_KEY = 'pending_email_verification';
const GOOGLE_POST_AUTH_DESTINATION_KEY = 'google_post_auth_destination';
const GOOGLE_REDIRECT_ERROR_KEY = 'google_redirect_error';

const getAuthErrorMessage = (error: unknown) => {
  const authError = error as { code?: string; message?: string };
  const code = authError.code || authError.message;
  if (!code) return 'An unexpected error occurred. Please try again.';

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Please check if your email or password is correct.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/email-already-in-use':
      return 'An account already exists with this email. Please sign in or use a different email.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is currently disabled.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/invalid-app-credential':
      return 'Verification failed. This typically happens due to network issues or if the site is not authorized in Firebase. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    default:
      // If it's a raw Firebase error like "Firebase: Error (auth/invalid-credential)."
      if (typeof code === 'string' && code.includes('invalid-credential')) {
        return 'Please check if your email or password is correct.';
      }
      return authError.message || 'Authentication failed. Please try again.';
  }
};

export default function Auth() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'login';
  const roleParam = searchParams.get('role') as 'candidate' | 'employer' | null;
  const returnUrl = searchParams.get('returnUrl');

  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    signIn,
    signUp,
    signInWithGoogle,
    user,
    loading,
    getUserRole,
  } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [emailDomainError, setEmailDomainError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    role: roleParam || ('candidate' as 'candidate' | 'employer'),
  });

  const isSignUp = mode === 'signup';

  // Real-time gmail block check for employer signup
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, email: value }));
    if (isSignUp && formData.role === 'employer' && value.includes('@')) {
      const domain = value.split('@')[1]?.toLowerCase();
      const isException = EMPLOYER_EMAIL_EXCEPTIONS.includes(value.toLowerCase());
      if (domain && GENERIC_EMAIL_DOMAINS.includes(domain) && !isException) {
        setEmailDomainError('Personal emails like @gmail.com are not allowed. Please use your company email.');
      } else {
        setEmailDomainError('');
      }
    } else {
      setEmailDomainError('');
    }
  };

  const passwordChecks = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /[0-9]/.test(formData.password),
    special: /[^A-Za-z0-9]/.test(formData.password),
  };
  const isPasswordStrong = Object.values(passwordChecks).every(Boolean);

  useEffect(() => {
    const checkUserAndRedirect = async () => {
      if (loading) return;

      if (user) {
        const postAuthDestination = localStorage.getItem(GOOGLE_POST_AUTH_DESTINATION_KEY);
        localStorage.removeItem(GOOGLE_POST_AUTH_DESTINATION_KEY);
        if (postAuthDestination && postAuthDestination.startsWith('/') && !postAuthDestination.startsWith('//')) {
          navigate(postAuthDestination);
          return;
        }
        if (user.email && !user.emailVerified) {
          navigate('/verify-email');
          return;
        }
        if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
          navigate(returnUrl);
          return;
        }
        const role = await getUserRole();
        // Force employer for super-admin account (env-var driven)
        const superAdminEmail = import.meta.env.VITE_SUPER_ADMIN_EMAIL as string | undefined;
        const isTestAccount = superAdminEmail && user.email?.toLowerCase() === superAdminEmail.toLowerCase();
        const effectiveRole = isTestAccount ? 'employer' : role;
        navigate(effectiveRole === 'employer' ? '/employer/dashboard' : '/candidate/dashboard');
      }
    };
    checkUserAndRedirect();
  }, [user, loading, navigate, getUserRole, returnUrl]);

  // Show any error that was stored during a Google redirect flow (mobile)
  useEffect(() => {
    const redirectErr = localStorage.getItem(GOOGLE_REDIRECT_ERROR_KEY);
    if (redirectErr) {
      localStorage.removeItem(GOOGLE_REDIRECT_ERROR_KEY);
      setIsLoading(false);
      toast({ variant: 'destructive', title: 'Google sign-in failed', description: redirectErr });
    }
  }, [toast]);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const role = isSignUp ? formData.role : undefined;
      const { isNewUser } = await signInWithGoogle(role, returnUrl ?? undefined);
      if (!isNewUser && !auth.currentUser) {
        // Redirect flow started — the page will reload after Google returns.
        return;
      }
      toast({ title: isNewUser ? 'Account created!' : 'Welcome back!' });
      if (isNewUser && formData.role === 'candidate') {
        const onboardingPath = returnUrl
          ? `/candidate/onboarding?returnUrl=${encodeURIComponent(returnUrl)}`
          : '/candidate/onboarding';
        navigate(onboardingPath);
      } else if (isNewUser) {
        navigate('/employer/onboarding');
      } else if (returnUrl) {
        navigate(returnUrl);
      } else {
        const userRole = await getUserRole();
        navigate(userRole === 'employer' ? '/employer/dashboard' : '/candidate/dashboard');
      }
    } catch (error: unknown) {
      toast({ variant: 'destructive', title: 'Google sign-in failed', description: getAuthErrorMessage(error) });
    } finally {
      setIsLoading(false);
    }
  };


  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (isSignUp && formData.role === 'employer') {
        const normalizedEmail = formData.email.trim().toLowerCase();
        const domain = normalizedEmail.split('@')[1];
        const isException = EMPLOYER_EMAIL_EXCEPTIONS.includes(normalizedEmail);
        if (!isException && GENERIC_EMAIL_DOMAINS.includes(domain)) {
          toast({
            variant: 'destructive',
            title: 'Company email required',
            description: 'Employer accounts require a company email. (Admin exception is allowed.)',
          });
          setIsLoading(false);
          return;
        }
      }
      if (isSignUp) {
        sessionStorage.setItem(PENDING_EMAIL_VERIFICATION_KEY, 'true');
        await signUp(formData.email, formData.password, formData.fullName, formData.role);
        // Fire GTM signup event
        if (formData.role === 'employer') analytics.employerSignUp();
        else analytics.candidateSignUp();
        // Store returnUrl so verify-email page can redirect after verification
        if (returnUrl) {
          sessionStorage.setItem('auth_return_url', returnUrl);
        }
        // Store role for post-verification redirect
        sessionStorage.setItem('auth_signup_role', formData.role);
        toast({
          title: 'Account created!',
          description: 'Please verify your email to continue.',
        });
        navigate('/verify-email');
      } else {
        const { user: signedInUser } = await signIn(formData.email, formData.password);

        // Email verification enforcement (restored for production security)
        if (signedInUser.email && !signedInUser.emailVerified) {
          sessionStorage.setItem(PENDING_EMAIL_VERIFICATION_KEY, 'true');
          toast({
            title: 'Verify your email',
            description: 'Please verify your email before continuing.',
          });
          navigate('/verify-email');
          return;
        }

        setTimeout(async () => {
          if (returnUrl) {
            navigate(returnUrl);
            return;
          }
          const role = await getUserRole();
          // Fire GTM login event
          if (role === 'employer') analytics.employerLogin();
          else analytics.candidateLogin();
          analytics.setUserProperties(role as 'employer' | 'candidate');
          toast({ title: 'Welcome back!' });
          navigate(role === 'employer' ? '/employer/dashboard' : '/candidate/dashboard');
        }, 500);
      }
    } catch (error: unknown) {
      if (isSignUp) {
        sessionStorage.removeItem(PENDING_EMAIL_VERIFICATION_KEY);
      }
      toast({
        variant: 'destructive',
        title: isSignUp ? 'Sign up failed' : 'Sign in failed',
        description: getAuthErrorMessage(error),
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="relative h-screen bg-white overflow-hidden text-slate-900">
      <SEO title="Sign In" noIndex />

      <main className="grid h-full lg:grid-cols-2">
        {/* ───── LEFT PANEL: Branding + Floating Paths (LIGHT) ───── */}
        <aside className="relative hidden h-full flex-col bg-slate-50 p-12 lg:flex overflow-hidden border-r border-slate-100">
          {/* Background overlay */}
          <div className="absolute inset-0 z-10 bg-gradient-to-t from-white/90 via-white/40 to-transparent" />

          {/* Paths */}
          <div className="absolute inset-0 z-0">
            <FloatingPaths position={1} />
            <FloatingPaths position={-1} />
            <div className="absolute top-[20%] left-[20%] w-[400px] h-[400px] bg-[radial-gradient(circle_at_center,rgba(13,148,136,0.2),rgba(255,255,255,0)_70%)] rounded-full -z-10" />
          </div>

          <Link to="/" className="z-20 flex items-center gap-2 group">
            <span className="text-3xl font-bold tracking-tight text-slate-900 font-display group-hover:text-accent transition-colors">
              <span className="text-[0.9em] opacity-80 mr-1.5">//</span>Intellr<span className="text-accent">.</span>
            </span>
          </Link>

          <div className="relative z-20 mt-auto">
            <motion.blockquote
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6 max-w-lg"
            >
              <div className="w-12 h-1 rounded-full bg-accent" />
              <p className="text-2xl font-light leading-relaxed text-slate-700 italic">
                &ldquo;Intellr<span className="text-accent">.</span> has completely transformed how we hire. The AI Voice Agent screens
                candidates 24/7, saving us hundreds of hours.&rdquo;
              </p>
              <footer className="text-[10px] font-bold font-mono text-slate-400 uppercase tracking-[0.2em]">
                ~ Neha Agarwal &middot; Head of Talent
              </footer>
            </motion.blockquote>
          </div>
        </aside>

        {/* ───── RIGHT PANEL: Auth Form (LIGHT PREMIUM) ───── */}
        <section className="relative flex flex-col h-full overflow-y-auto bg-white">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[radial-gradient(circle_at_center,rgba(13,148,136,0.15),rgba(255,255,255,0)_70%)] rounded-full pointer-events-none" />

          <div className="p-6 relative z-20">
            <Button variant="ghost" size="sm" asChild className="h-8 text-slate-400 hover:text-slate-900">
              <Link to="/" className="flex items-center text-[10px] font-bold uppercase tracking-widest">
                <ChevronLeftIcon className="h-3 w-3 mr-1.5" />
                Back
              </Link>
            </Button>
          </div>

          <div className="flex-1 flex flex-col justify-center px-8 pb-10 sm:px-16 relative z-10">
            <div className="mx-auto w-full max-w-[440px] space-y-6">
              {/* Mobile Branding */}
              <div className="flex items-center gap-2 lg:hidden mb-8">
                <span className="text-2xl font-bold font-display tracking-tight text-slate-900">
                  <span className="text-[0.9em] opacity-80 mr-1.5">//</span>Intellr<span className="text-accent">.</span>
                </span>
              </div>

              <div className="space-y-2">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-slate-50 text-slate-500 text-[8px] font-bold uppercase tracking-widest border border-slate-200"
                >
                  <Sparkles className="w-3 h-3 text-accent" />
                  Secure Access
                </motion.div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                  {isSignUp ? 'Create account' : 'Welcome back'}
                </h1>
                <p className="text-slate-500 font-medium text-sm">
                  {isSignUp
                    ? 'Join the network and hire faster with AI.'
                    : 'Manage your talent pipeline with ease.'}
                </p>
              </div>

              {/* Role Selection */}
              {isSignUp && (
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { id: 'candidate', label: 'Candidate', icon: User },
                    { id: 'employer', label: 'Employer', icon: Building2 },
                  ].map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => setFormData((d) => ({ ...d, role: role.id as 'candidate' | 'employer' }))}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-2xl border-2 transition-all duration-300 text-left',
                        formData.role === role.id
                          ? 'border-accent bg-accent/5 text-accent shadow-lg shadow-accent/5'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm text-slate-700'
                      )}
                    >
                      <div className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                        formData.role === role.id ? "bg-accent/10" : "bg-slate-100"
                      )}>
                        <role.icon
                          className={cn(
                            'h-5 w-5',
                            formData.role === role.id ? 'text-accent' : 'text-slate-500'
                          )}
                        />
                      </div>
                      <div className="flex flex-col">
                        <span className={cn(
                          "text-[10px] font-bold uppercase tracking-widest leading-none",
                          formData.role === role.id ? "text-slate-900" : "text-slate-700"
                        )}>
                          {role.label}
                        </span>
                        <span className={cn(
                          "text-[8px] font-medium mt-1 leading-none",
                          formData.role === role.id ? "text-slate-500" : "text-slate-400"
                        )}>
                          {role.id === 'candidate' ? 'I want to be hired' : 'I want to hire'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div className="flex justify-center">
                <button
                  className="gsi-material-button"
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                >
                  <div className="gsi-material-button-state"></div>
                  <div className="gsi-material-button-content-wrapper">
                    <div className="gsi-material-button-icon">
                      <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="20" height="20" style={{ display: 'block' }}>
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                        <path fill="none" d="M0 0h48v48H0z"></path>
                      </svg>
                    </div>
                    <span className="gsi-material-button-contents">Sign in with Google</span>
                    <span style={{ display: 'none' }}>Sign in with Google</span>
                  </div>
                </button>
              </div>

              <AuthSeparator />

              {/* Form Area */}
              <form onSubmit={handleEmailSubmit} className="space-y-3">
                {isSignUp && (
                  <div className="relative">
                    <Input
                      value={formData.fullName}
                      onChange={(e) => setFormData((d) => ({ ...d, fullName: e.target.value }))}
                      placeholder="Your Full Name"
                      className="ps-11 h-12 bg-slate-50 border-slate-200 focus:border-accent text-slate-900 rounded-xl"
                      required
                    />
                    <User className="absolute left-4 top-4 h-4 w-4 text-slate-400" />
                  </div>
                )}
                <div className="relative">
                  <Input
                    value={formData.email}
                    onChange={handleEmailChange}
                    placeholder={isSignUp && formData.role === 'employer' ? 'Work email (e.g. you@company.com)' : 'Email address'}
                    className={cn(
                      "ps-11 h-12 bg-slate-50 border-slate-200 focus:border-accent text-slate-900 rounded-xl",
                      emailDomainError && "border-red-400 focus:border-red-400"
                    )}
                    type="email"
                    required
                  />
                  <Mail className="absolute left-4 top-4 h-4 w-4 text-slate-400" />
                  {emailDomainError && (
                    <p className="mt-1.5 text-[11px] font-semibold text-red-500 flex items-center gap-1.5">
                      <span className="w-3.5 h-3.5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 text-[9px] text-red-500 font-black">!</span>
                      {emailDomainError}
                    </p>
                  )}
                </div>
                <div className="relative">
                  <Input
                    value={formData.password}
                    onChange={(e) => setFormData((d) => ({ ...d, password: e.target.value }))}
                    placeholder="Password"
                    className="ps-11 h-12 bg-slate-50 border-slate-200 focus:border-accent text-slate-900 rounded-xl"
                    type="password"
                    required
                    minLength={8}
                  />
                  <Lock className="absolute left-4 top-4 h-4 w-4 text-slate-400" />
                </div>

                {!isSignUp && (
                  <div className="flex justify-end pr-1">
                    <Link
                      to="/forgot-password"
                      className="text-xs text-accent font-bold hover:underline transition-all"
                    >
                      Forgot password?
                    </Link>
                  </div>
                )}

                {isSignUp && formData.password.length > 0 && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-2">
                    {[
                      { key: 'length', label: '8+ chars' },
                      { key: 'uppercase', label: 'ABC' },
                      { key: 'lowercase', label: 'abc' },
                      { key: 'number', label: '123' },
                      { key: 'special', label: '!@#' },
                    ].map(({ key, label }) => (
                      <span
                        key={key}
                        className={cn(
                          'text-[8px] font-bold uppercase tracking-widest flex items-center gap-1.5',
                          passwordChecks[key as keyof typeof passwordChecks]
                            ? 'text-accent'
                            : 'text-slate-300'
                        )}
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        {label}
                      </span>
                    ))}
                  </div>
                )}

                <Button
                  type="submit"
                  variant="hero"
                  className="w-full h-14 text-base font-bold shadow-2xl shadow-accent/25"
                  disabled={isLoading || (isSignUp && !isPasswordStrong)}
                >
                  {isLoading && <Loader2 className="h-5 w-5 animate-spin mr-3" />}
                  {isSignUp ? 'Activate Account' : 'Sign In'}
                </Button>
              </form>

              <p className="text-center text-sm text-slate-500 pt-4 font-medium">
                {isSignUp ? (
                  <>
                    Already a member?{' '}
                    <Link
                      to={`/auth?mode=login${returnUrl ? `&returnUrl=${encodeURIComponent(returnUrl)}` : ''
                        }`}
                      className="text-accent font-bold hover:text-accent/80 transition-colors"
                    >
                      Sign in
                    </Link>
                  </>
                ) : (
                  <>
                    Don't have an account?{' '}
                    <Link
                      to={`/auth?mode=signup${returnUrl ? `&returnUrl=${encodeURIComponent(returnUrl)}` : ''
                        }`}
                      className="text-accent font-bold hover:text-accent/80 transition-colors"
                    >
                      Sign up
                    </Link>
                  </>
                )}
              </p>

              {isSignUp && (
                <p className="text-center text-[10px] text-slate-400 leading-relaxed px-6 font-bold uppercase tracking-tight">
                  By joining, you agree to our{' '}
                  <Link
                    to="/terms"
                    className="text-slate-600 hover:text-slate-900 underline underline-offset-2"
                  >
                    Terms
                  </Link>{' '}
                  and{' '}
                  <Link
                    to="/privacy"
                    className="text-slate-600 hover:text-slate-900 underline underline-offset-2"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
